/**
 * Agente di sistema per gli allarmi (ADR 0034), servito da `agents`.
 *
 * L'agente riceve gli allarmi di Alertmanager, chiede da solo il riavvio di un set in
 * crash e propone il resto all'amministratore. Da qui un `ADMIN` di piattaforma legge gli
 * incidenti, approva un'azione proposta (riavvio o sospensione di un set) o chiude un
 * incidente. Agli altri il servizio risponde 403.
 *
 * Provvisorio dichiarato: percorsi e tipi scritti a mano (CLAUDE.md §2.3), presidiati da
 * `systemAgent.test.ts`.
 */

import type { ApiClient } from '../client';

/** Percorso degli incidenti. */
const INCIDENTS_PATH = '/api/v1/system-agent/incidents';

/** Filtro dell'elenco. */
export type IncidentFilter = 'open' | 'closed' | 'all';

/** Esito della valutazione: registrato, azione fatta, proposta. */
export type IncidentTriage = 'noted' | 'acted' | 'proposed';

/** Stato della proposta. */
export type ProposalStatus = 'none' | 'pending' | 'approved' | 'dismissed' | 'expired' | 'failed';

/** Un incidente dell'agente di sistema. */
export interface SystemIncident {
  /** Incidente. */
  id: string;
  /** Nome dell'allarme. */
  alertname: string;
  /** Gravità dichiarata dalla regola, vuota se manca. */
  severity: string;
  /** Stato dell'allarme. */
  alert_status: 'firing' | 'resolved';
  /** Riassunto della regola. */
  summary: string;
  /** Tipo del bersaglio. */
  target_kind: 'set' | 'hosting' | 'service' | 'data' | 'storage' | 'other';
  /** Riferimento del bersaglio (id del set, namespace, servizio). */
  target_ref: string;
  /** Bersaglio in forma leggibile. */
  target_label: string;
  /** Esito della valutazione. */
  triage: IncidentTriage;
  /** Diagnosi. */
  diagnosis: string;
  /** Azione eseguita dall'agente, vuota se nessuna. */
  action_taken: string;
  /** Azione approvabile proposta, vuota se nessuna. */
  proposed_action: '' | 'restart_set' | 'suspend_set';
  /** Proposta in una frase, vuota se nessuna. */
  proposal: string;
  /** Stato della proposta. */
  proposal_status: ProposalStatus;
  /** Stato per l'amministratore. */
  state: 'open' | 'closed';
  /** Consegne ricevute per lo stesso allarme. */
  deliveries: number;
  /** Prima consegna, ISO 8601. */
  received_at: string;
  /** Risoluzione dell'allarme, ISO 8601. */
  resolved_at: string | null;
  /** Chiusura, ISO 8601. */
  closed_at: string | null;
}

/** Una pagina di incidenti con lo stato dell'agente. */
export interface IncidentPage {
  /** True se l'agente è acceso e ha il segreto del webhook. */
  enabled: boolean;
  /** True se esegue da solo le azioni della lista chiusa. */
  auto_actions: boolean;
  /** Incidenti da gestire. */
  open_count: number;
  /** Incidenti, dal più recente. */
  items: SystemIncident[];
  /** Incidenti che rispondono al filtro. */
  total: number;
}

/** Client dell'agente di sistema. */
export class SystemAgentApi {
  /**
   * @param client Client HTTP verso il gateway.
   */
  constructor(private readonly client: ApiClient) {}

  /**
   * Una pagina di incidenti.
   *
   * @param state Filtro.
   * @param offset Incidenti da saltare.
   * @returns La pagina e lo stato dell'agente.
   */
  list(state: IncidentFilter = 'open', offset = 0): Promise<IncidentPage> {
    const params = new URLSearchParams({ state, limit: '50' });
    if (offset > 0) {
      params.set('offset', String(offset));
    }
    return this.client.request<IncidentPage>('GET', `${INCIDENTS_PATH}?${params.toString()}`);
  }

  /**
   * Esegue l'azione proposta da un incidente.
   *
   * @param incidentId Incidente.
   * @returns L'incidente aggiornato; `proposal_status` dice se l'azione è riuscita.
   */
  approve(incidentId: string): Promise<SystemIncident> {
    return this.client.request<SystemIncident>('POST', `${INCIDENTS_PATH}/${encodeURIComponent(incidentId)}/approve`);
  }

  /**
   * Chiude un incidente; una proposta in attesa non si esegue più.
   *
   * @param incidentId Incidente.
   * @returns L'incidente chiuso.
   */
  close(incidentId: string): Promise<SystemIncident> {
    return this.client.request<SystemIncident>('POST', `${INCIDENTS_PATH}/${encodeURIComponent(incidentId)}/close`);
  }
}
